import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CONVERSATION_TITLE_MAX,
  type ConversationAnchorType,
  type ConversationThreadView,
  type ConversationVisibility,
} from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { useCan, useRefusal } from '../../lib/permissions.js';
import { useFilesMeta } from '../files/model.js';
import { Composer, mentionablesFrom, referenceablesFrom, type AttachOptions } from './Composer.js';
import { ANCHOR_NOUN, newRequestKey, useConversationLocations, useMyUserId, usePeople } from './hooks.js';

/** Said BEFORE a private conversation exists, and again wherever people are added (O8). */
export const STAFF_ACCESS_NOTICE =
  'Only the people you add can read this, plus ScoutAround staff for moderation and support. Every staff access is recorded.';

/**
 * Start a conversation on one anchor. One request key per opened form, so a
 * submit retried after a lost answer returns the conversation it already made
 * instead of a second one. The first message may carry files (2026-11-05): each is
 * uploaded before the conversation exists, and the create attaches them.
 */
export function NewThreadForm({
  projectId,
  anchorType,
  anchorId,
  onCreated,
  onCancel,
}: {
  projectId: string;
  anchorType: ConversationAnchorType;
  anchorId: string;
  onCreated: (thread: ConversationThreadView) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const canPost = useCan('CONVERSATION_POST');
  const refusal = useRefusal('CONVERSATION_POST');
  const me = useMyUserId();
  const people = usePeople(projectId, anchorType);
  const locations = useConversationLocations(projectId);
  const referenceable = useMemo(() => referenceablesFrom(locations.data), [locations.data]);
  const requestKey = useRef(newRequestKey());
  // As on a reply: the Files tab's limit and types, and whether the plan stores files.
  const filesMeta = useFilesMeta(projectId);
  const attach: AttachOptions = {
    limit: filesMeta.data?.upload,
    allowed: filesMeta.data ? filesMeta.data.upload.allowed && !filesMeta.data.readOnly : true,
    upload: (file, clientRequestId, control) =>
      api.uploadConversationAttachment(projectId, null, file, { clientRequestId }, control),
  };

  const [title, setTitle] = useState('');
  const [visibility, setVisibility] = useState<ConversationVisibility>('PUBLIC');
  const [invited, setInvited] = useState<number[]>([]);

  // Who may join: a member who can read a public thread on this anchor.
  const joinable = (people.data?.people ?? []).filter(
    (p) => p.memberStatus === 'ACTIVE' && p.userId !== me && p.canReadAnchorThreads === true,
  );

  const create = useMutation({
    mutationFn: ({ body, attachmentIds }: { body: string; attachmentIds: readonly string[] }) =>
      api.createConversation(projectId, {
        clientRequestId: requestKey.current,
        anchorType,
        anchorId,
        title: title.trim(),
        visibility,
        body,
        participantUserIds: visibility === 'PRIVATE' ? invited : [],
        attachmentIds,
      }),
    onSuccess: (thread) => {
      void queryClient.invalidateQueries({ queryKey: ['conversation-summary', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations', projectId] });
      onCreated(thread);
    },
  });

  if (!canPost) {
    return (
      <p className="mm" data-testid="conversation-new-refused" style={{ margin: 0 }}>
        {refusal ?? 'You cannot start conversations on this production.'}
      </p>
    );
  }

  const mentionable =
    visibility === 'PRIVATE'
      ? mentionablesFrom(people.data?.people, (p) => invited.includes(p.userId), me)
      : mentionablesFrom(people.data?.people, (p) => p.canReadAnchorThreads === true, me);

  return (
    <div data-testid="conversation-new" style={{ display: 'grid', gap: 14 }}>
      <label style={{ display: 'grid', gap: 6, fontSize: 13 }}>
        <span>Title</span>
        <input
          data-testid="conversation-new-title"
          value={title}
          maxLength={CONVERSATION_TITLE_MAX}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
          placeholder={`What about this ${ANCHOR_NOUN[anchorType]}?`}
          style={{ padding: 8, border: '1px solid var(--color-neutral-300)', fontSize: 14 }}
        />
      </label>

      <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        <legend style={{ fontSize: 13, marginBottom: 6 }}>Who can read it</legend>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
          <input
            type="radio"
            name="conversation-visibility"
            data-testid="conversation-new-public"
            checked={visibility === 'PUBLIC'}
            onChange={() => {
              setVisibility('PUBLIC');
            }}
          />
          <span>
            <strong>Public</strong> — everyone on the production who can see this {ANCHOR_NOUN[anchorType]}.
          </span>
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
          <input
            type="radio"
            name="conversation-visibility"
            data-testid="conversation-new-private"
            checked={visibility === 'PRIVATE'}
            onChange={() => {
              setVisibility('PRIVATE');
            }}
          />
          <span>
            <strong>Private</strong> — only the people you add.
          </span>
        </label>
        {visibility === 'PRIVATE' ? (
          <p
            data-testid="conversation-new-staff-notice"
            style={{ margin: 0, fontSize: 12.5, padding: '8px 10px', background: 'var(--color-neutral-100)', borderLeft: '3px solid var(--color-neutral-400)' }}
          >
            {STAFF_ACCESS_NOTICE}
          </p>
        ) : null}
      </fieldset>

      {visibility === 'PRIVATE' ? (
        <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          <legend style={{ fontSize: 13, marginBottom: 6 }}>People</legend>
          {people.isPending ? <p className="mm" style={{ margin: 0 }}>Loading the team…</p> : null}
          {joinable.length === 0 && !people.isPending ? (
            <p className="mm" data-testid="conversation-new-nobody" style={{ margin: 0, fontSize: 13 }}>
              Nobody else on the production can see this {ANCHOR_NOUN[anchorType]}.
            </p>
          ) : null}
          {joinable.map((person) => (
            <label key={person.userId} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input
                type="checkbox"
                data-testid={`conversation-new-person-${String(person.userId)}`}
                checked={invited.includes(person.userId)}
                onChange={(event) => {
                  setInvited((current) =>
                    event.target.checked ? [...current, person.userId] : current.filter((id) => id !== person.userId),
                  );
                }}
              />
              {person.displayName ?? 'Member'}
            </label>
          ))}
        </fieldset>
      ) : null}

      <Composer
        testId="conversation-new-message"
        mentionable={mentionable}
        referenceable={referenceable}
        submitLabel="Start conversation"
        placeholder="The first message"
        attach={attach}
        onSend={async (body, attachmentIds) => {
          if (title.trim() === '') throw new Error('Give the conversation a title.');
          await create.mutateAsync({ body, attachmentIds });
        }}
      />
      <div>
        <button type="button" className="ctl" data-testid="conversation-new-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
